import { useState, useCallback } from 'react';
import { subscriptionService, SubscriptionStatus } from '@/services/subscription.service';
import { notificationService } from '@/services/notification.service';
import { useAuthProvider } from '@/pages/auth/hooks/useAuthProvider';

export const useSubscriptionCheck = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const { handleRefreshSession } = useAuthProvider();

  const checkSubscription = useCallback(async (onSuccess: () => void) => {
    try {
      setIsCheckingSubscription(true);
      const status = await subscriptionService.getSubscriptionStatus();
      
      if (status === SubscriptionStatus.ACTIVE) {
        onSuccess();
      } else {
        const retryStatus = await retrySubscriptionCheck();
        if (retryStatus === SubscriptionStatus.ACTIVE) {
          onSuccess();
        } else {
          setIsModalOpen(true);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error checking subscription status');
      }
    } finally {
      setIsCheckingSubscription(false);
    }
  }, [handleRefreshSession]);

  const retrySubscriptionCheck = async (): Promise<SubscriptionStatus> => {
    try {
      await handleRefreshSession();
      return await subscriptionService.getSubscriptionStatus();
    } catch (error) {
      return SubscriptionStatus.INACTIVE;
    }
  };

  const forceRefreshSubscription = useCallback(async () => {
    setIsCheckingSubscription(true);
    try {
      await handleRefreshSession();
      
      let status = SubscriptionStatus.INACTIVE;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (status !== SubscriptionStatus.ACTIVE && attempts < maxAttempts) {
        try {
          status = await subscriptionService.getSubscriptionStatus();
          if (status === SubscriptionStatus.ACTIVE) {
            setIsModalOpen(false);
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.warn('Error checking subscription status, retrying...', error);
        }
        attempts++;
      }
      
      if (status !== SubscriptionStatus.ACTIVE) {
        console.info('Subscription not yet active, but payment was successful. Assuming it will be active soon.');
      }
    } catch (error) {
      console.error('Error refreshing subscription status', error);
    } finally {
      setIsCheckingSubscription(false);
    }
  }, [handleRefreshSession, setIsModalOpen]);

  return {
    isModalOpen,
    setIsModalOpen,
    isCheckingSubscription,
    checkSubscription,
    forceRefreshSubscription
  };
}; 